import { useCollabItemContext } from 'components/CollabItem/CollabItem'
import { Item } from 'components/CollabItem/Item'
import { observer } from 'mobx-react-lite'
import dayjs from 'dayjs'
import { TrackedChangeCommentItemProps } from './Types'
import { TrackedChangeCommentItemEditForm } from '../TrackedChangeCommentItemEditForm/TrackedChangeCommentItemEditForm'
import { useDocStore } from 'stores/DocStore'
import { ActionMenuControl } from './ActionMenuControl/ActionMenuControl'
import { parseUrlsInString } from 'utils/urlUtils'
import { parseCommentMentionsToHTML } from 'utils/comments'
import { useToggleTrackedChangeCommentReaction } from 'hooks/queries/trackedChangeComments/useToggleTrackedChangeCommentReaction'
import { ReactControl } from './ReactControl/ReactControl'
import { ProseDisplay } from 'common/ProseInput/ProseDisplay'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import CommentStyles from 'components/CommentV2/CommentItem/CommentItem.module.scss'
import cx from 'classnames'

const TrackedChangeCommentItem = observer(({ comment, inCleanupMode = false }: TrackedChangeCommentItemProps) => {
  const docStore = useDocStore()
  const { editing } = useCollabItemContext()
  const isEditing = editing?.id === comment.id
  const { mutate: toggleReaction } = useToggleTrackedChangeCommentReaction({
    articleKey: docStore.articleId,
    layerId: docStore.layerId || '',
    comment,
  })
  const richText = useFlipperFlag('richtext_comments')

  return (
    <Item.Root editing={isEditing} className={cx(CommentStyles.replyItemRoot, CommentStyles.itemRoot)}>
      {!isEditing && docStore.layerId && (
        <Item.ControlGroup>
          {!inCleanupMode && (
            <ActionMenuControl comment={comment} articleKey={docStore.articleId} layerId={docStore.layerId} />
          )}
          <ReactControl comment={comment} />
        </Item.ControlGroup>
      )}
      <Item.Header>
        <Item.Avatar url={comment.user.avatar_url || undefined} name={comment.user.display_name} />
        <p>
          <Item.Actor name={comment.user.display_name} actorId={comment.user.id} capitalize />{' '}
          <span className={CommentStyles.metaData}>{dayjs(comment.created_at).format('MMM D h:mma')}</span>
        </p>
      </Item.Header>
      {isEditing && docStore.layerId && !inCleanupMode && (
        <TrackedChangeCommentItemEditForm
          comment={comment}
          articleKey={docStore.articleId}
          layerId={docStore.layerId}
        />
      )}
      {!isEditing && (
        <>
          <Item.Body>
            {richText ? (
              <ProseDisplay html={comment.html_body || comment.body} />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: parseUrlsInString(parseCommentMentionsToHTML(comment.body)) }} />
            )}
          </Item.Body>
          <Item.Reactions reactions={comment.reactions} onToggleReaction={toggleReaction} />
        </>
      )}
    </Item.Root>
  )
})

export { TrackedChangeCommentItem }
